import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismButton, PrismNumberInput } from '@mylighthouse/prism-ember';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import { eq, or } from 'ember-truth-helpers';
import { timeout } from 'lumen/utils/waiters';

interface SettingsCentralMappingTablesCellsEditableNumberCellSignature {
  Args: {
    cellDataTestId?: string;
    isMixed?: boolean;
    isReadOnly?: boolean;
    maxValue: number;
    placeholder: string;
    setValue: (value: number | null) => void;
    value: number | null;
  };
}

export default class SettingsCentralMappingTablesCellsEditableNumberCell extends Component<SettingsCentralMappingTablesCellsEditableNumberCellSignature> {
  @tracked isEditing = false;

  // ember/no-tracked-properties-from-args rule mentions:
  // "The @tracked property will not be updated when the args change, which is almost never what you want"
  // however, in this case, it is what we want.
  // eslint-disable-next-line ember/no-tracked-properties-from-args
  @tracked tempValue: number | null = this.args.value;

  onBlurTask = task({ restartable: true }, async (): Promise<void> => {
    await timeout('extra-short');

    if (this.tempValue === this.args.value) {
      this.isEditing = false;
      return;
    }
    this.setCurrentValue();
  });

  get inEditMode(): boolean {
    return this.isReadOnly === false && this.isEditing;
  }

  get isReadOnly(): boolean {
    return this.args.isReadOnly === true;
  }

  get isTempValueValid(): boolean {
    return this.tempValue === null || this.tempValue >= 0;
  }

  get showEditButton(): boolean {
    return this.isReadOnly === false && !this.isEditing;
  }

  setCurrentValue(): void {
    const { value, setValue } = this.args;

    // Fix for `onKeyDown`: when pressing Enter, `setCurrentValue` is called.
    // However, the `onBlur` of the input also triggers, which also calls `setCurrentValue`.
    // To avoid it being executed twice and Ember complaining about the `isEditing` property
    // being changed twice in the same run loop, we return early if the `setCurrentValue` has already been called.
    if (!this.isEditing) {
      return;
    }

    if (!this.isTempValueValid) {
      this.tempValue = value;
      this.isEditing = false;
      return;
    }

    // If the value is greater than the max value, set it to the max value.
    if (this.tempValue && this.tempValue > this.args.maxValue) {
      this.tempValue = this.args.maxValue;
    }

    if (setValue) {
      setValue(this.tempValue);
    }

    this.isEditing = false;
  }

  @action onClickEdit(): void {
    // If the cell is mixed, the tempValue should be set to null before editing.
    if (this.args.isMixed) {
      this.tempValue = null;
    }

    this.isEditing = true;
  }

  @action onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.setCurrentValue();
    }
  }

  <template>
    <div
      class="central-mapping__table-mapping__td-number flex flex-row items-center gap-300"
      data-test-id={{@cellDataTestId}}
    >
      {{#if this.inEditMode}}
        <PrismNumberInput
          @autofocus={{true}}
          @max={{@maxValue}}
          @min={{0}}
          @onValueChange={{fn (mut this.tempValue)}}
          @placeholder={{@placeholder}}
          @removePadding={{true}}
          @step={{1}}
          @value={{this.tempValue}}
          data-test-id="central-mapping-td-number-input"
          {{on "blur" this.onBlurTask.perform}}
          {{on "keydown" this.onKeyDown}}
        />
      {{else}}
        <p class="text-200--regular">
          {{#if (or @value (eq @value 0))}}
            {{@value}}
          {{else}}
            {{#if @isMixed}}
              {{t "settings.central_mapping.mapping.mixed"}}
            {{else}}
              {{t "general.missing"}}
            {{/if}}
          {{/if}}
        </p>
      {{/if}}

      {{#if this.showEditButton}}
        <div class="central-mapping__table-mapping__td-number__button-edit">
          <PrismButton
            @buttonType="ghost"
            @icon="edit-pencil"
            @size="small"
            data-test-id="central-mapping-td-number-edit-button"
            {{on "click" this.onClickEdit}}
          />
        </div>
      {{/if}}
    </div>
  </template>
}
